import React from "react";
import { Box, Typography, IconButton, Tooltip, Divider } from "@mui/material";
import DashboardCard from "../components/DashboardCard";
import ApiLoading from "../components/ApiLoading";
import { useWafrData } from "./useWafrData";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { NextPlanOutlined } from "@mui/icons-material";

const WAFRInsight = () => {
  const history = useHistory();
  const { data, loading, error } = useWafrData();

  // Always show 0s if error
  let wafr = {};
  if (error) {
    wafr = {
      totalWorkloads: 0,
      workloadsWithHighRisks: 0,
      totalHighRisks: 0,
      workloadsWithMediumRisks: 0,
      totalMediumRisks: 0,
      totalWorkloadsInProgress: 0,
      totalWorkloadsCompleted: 0,
    };
  } else {
    wafr = data?.data || {};
  }

  const totalWorkloads = wafr.totalWorkloads ?? 0;
  const workloadsHighRisk = wafr.workloadsWithHighRisks ?? 0;
  const totalHighRiskIssues = wafr.totalHighRisks ?? 0;
  const workloadsMediumRisk = wafr.workloadsWithMediumRisks ?? 0;
  const totalMediumRiskIssues = wafr.totalMediumRisks ?? 0;
  const totalWorkloadsInProgress = wafr.totalWorkloadsInProgress ?? 0;
  const totalWorkloadsCompleted = wafr.totalWorkloadsCompleted ?? 0;

  const handleGoTo = () => {
    history.push("/workload-assessment/");
  };

  // Small progress circle component
  const SmallProgressCircle = ({ completed, inProgress, total }) => {
    if (total === 0) return null;
    
    const completedPercentage = total > 0 ? (completed / total) * 100 : 0;
    const inProgressPercentage = total > 0 ? (inProgress / total) * 100 : 0;
    const radius = 8;
    const circumference = 2 * Math.PI * radius;
    const completedDashArray = (completedPercentage / 100) * circumference;
    const inProgressDashArray = (inProgressPercentage / 100) * circumference;
    
    const tooltipContent = (
      <Box sx={{ p: 1, minWidth: 150 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Assessment Status
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Completed</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>{completed}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">In Progress</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff9800' }}>{inProgress}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">Total</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{total}</Typography>
        </Box>
      </Box>
    );
    
    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
              '& .MuiTooltip-arrow': {
                color: 'background.paper',
                '&::before': {
                  border: '1px solid',
                  borderColor: 'divider',
                }
              }
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, cursor: 'pointer' }}>
          <svg width="20" height="20" style={{ transform: 'rotate(-90deg)' }}>
            {/* Background circle */}
            <circle
              cx="10"
              cy="10"
              r={radius}
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="2"
            />
            {/* Completed progress */}
            <circle
              cx="10"
              cy="10"
              r={radius}
              fill="none"
              stroke="#4caf50"
              strokeWidth="2"
              strokeDasharray={`${completedDashArray} ${circumference}`}
              strokeLinecap="round"
            />
            {/* In Progress (starts after completed) */}
            <circle
              cx="10"
              cy="10"
              r={radius}
              fill="none"
              stroke="#ff9800"
              strokeWidth="2"
              strokeDasharray={`${inProgressDashArray} ${circumference}`}
              strokeDashoffset={-completedDashArray}
              strokeLinecap="round"
            />
          </svg>
        </Box>
      </Tooltip>
    );
  };

  return (
    <DashboardCard
      title="WAFR"
      actions={[
        {
          label: "go-to",
          icon: (
            <Tooltip title="Open Detailed View">
              <IconButton
                onClick={handleGoTo}
                size="small"
                sx={{ p: 0, color: "text.secondary", transform: "rotate(-45deg)" }}
              >
                <NextPlanOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ]}
    >
      {loading ? (
        <ApiLoading variant="card" height={150} width="100%" />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2">Total Assessments</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmallProgressCircle 
                completed={totalWorkloadsCompleted}
                inProgress={totalWorkloadsInProgress}
                total={totalWorkloads}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{totalWorkloads}</Typography>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2">Assessments with High Risk Issues</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{workloadsHighRisk}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2">Total High Risk Issues</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{totalHighRiskIssues}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2">Assessments with Medium Risk Issues</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{workloadsMediumRisk}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2">Total Medium Risk Issues</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{totalMediumRiskIssues}</Typography>
          </Box>
        </Box>
      )}
    </DashboardCard>
  );
};

export default WAFRInsight; 